import React, { useEffect, useRef, useState } from 'react';
import { Award, Star, Users, ShieldCheck } from 'lucide-react';

// Calcula anos desde a fundação (Junho/2008) — atualiza automaticamente
const FOUNDATION = new Date(2008, 5, 1); // Junho = mês 5 (0-indexed)
const calcYears = () => {
  const now = new Date();
  let years = now.getFullYear() - FOUNDATION.getFullYear();
  const monthDiff = now.getMonth() - FOUNDATION.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < FOUNDATION.getDate())) years--;
  return Math.max(0, years);
};

const STATS = [
  { icon: Users,       value: 200,          suffix: '+', label: 'atendimentos\nno último mês', color: 'text-cyan-400'    },
  { icon: Star,        value: 5.0,          suffix: '★', label: 'avaliação Google\n(50+ reviews)', color: 'text-yellow-400', decimals: 1 },
  { icon: Award,       value: calcYears(),  suffix: '+', label: 'anos de\nexperiência',         color: 'text-emerald-400' },
  { icon: ShieldCheck, value: 100,          suffix: '%', label: 'satisfação\ngarantida',         color: 'text-blue-400'    },
];

const useCountUp = (target, decimals = 0, duration = 1400) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(target * eased);
            if (p < 1) requestAnimationFrame(tick);
            else setVal(target);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  return [ref, decimals > 0 ? val.toFixed(decimals) : Math.round(val)];
};

const StatItem = ({ stat }) => {
  const [ref, value] = useCountUp(stat.value, stat.decimals || 0);
  const Icon = stat.icon;

  return (
    <div
      ref={ref}
      className="flex items-center gap-2.5 bg-white/5 hover:bg-white/8 backdrop-blur-md border border-white/10 rounded-xl px-3.5 py-2.5 transition-all duration-300"
      data-testid={`hero-stat-${stat.label.split('\n')[0]}`}
    >
      <Icon className={`w-5 h-5 shrink-0 ${stat.color}`} />
      <div className="leading-tight">
        <div className="flex items-baseline gap-0.5">
          <span className={`text-xl sm:text-2xl font-extrabold tabular-nums ${stat.color}`}>
            {value}
          </span>
          <span className={`text-base font-bold ${stat.color}`}>{stat.suffix}</span>
        </div>
        <p className="text-[10px] sm:text-[11px] text-slate-300 leading-tight whitespace-pre-line">
          {stat.label}
        </p>
      </div>
    </div>
  );
};

const HeroTrustBadge = () => (
  <div
    className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-3 mt-6"
    data-testid="hero-trust-stats"
  >
    {STATS.map(s => <StatItem key={s.label} stat={s} />)}
  </div>
);

export default HeroTrustBadge;
